import {
  Opportunity,
  getEffectiveSeverity,
  OpportunityStatus,
  SortingOption,
} from "@/opportunity/opportunity";
import useOpportunityStatuses from "@/opportunity/useOpportunityStatuses";
import { Card } from "@/components/ui/card";
import { format, parseISO } from "date-fns";
import {
  ArrowRightLeftIcon, ChevronDown, ChevronUp,
  ExternalLinkIcon, InfoIcon,
  MapPinIcon, Maximize2Icon, Minimize2Icon,
  XIcon, ThumbsUp,
} from "lucide-react";
import QuickTooltip from "@/components/quick-tooltip";
import React, { Dispatch, SetStateAction, Suspense, useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ALL_SORTING_OPTIONS } from "@/opportunity/opportunity_table/columns";
import { Spinner } from "@/components/ui/spinner";
import { useOpportunities, useSubmitFeedback } from "@/opportunity/api";
import { useOpportunityManagement } from "@/opportunity/OpportunityManagementContext";
import StaffOnly from "@/user/StaffOnly";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import Filters from "@/opportunity/opportunity_table/Filters";
import { OpportunityManagementViewSelector } from "@/opportunity/OpportunityManagementViewSelector";
import UsefulFeedbackModal from "@/opportunity/UsefulFeedbackModal";
import { BellOff } from "lucide-react";


// ─── Severity pill classes ────────────────────────────────────────────────────

const severityPillClass: Record<string, string> = {
  red: "bg-red-100 text-red-800 border border-red-200",
  yellow: "bg-yellow-100 text-yellow-800 border border-yellow-200",
};

// ─── Board Card ───────────────────────────────────────────────────────────────

const OpportunityCard = ({ opportunity, isSnoozed = false }: { opportunity: Opportunity, isSnoozed?: boolean }) => {
  const { openOpportunity, openChangeOpportunityStatusModal } = useOpportunityManagement();
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const severity = getEffectiveSeverity(opportunity);

  const locationLabel = opportunity.installation.display_name_with_equipment
    ? `${opportunity.installation.display_name_with_equipment}`
    : `${opportunity.location.name} ${opportunity.installation.name}`;

  return (
    <>
    <Card className={`p-2 flex flex-col gap-2 ${isSnoozed ? "opacity-50" : ""} ${severity === "red" ? "border-l-4 border-l-red-500" : severity === "yellow" ? "border-l-4 border-l-yellow-500" : ""}`}>
      {/* Header: severity pill + failure pattern pill */}
      <div className="flex flex-wrap items-center gap-1.5">
        {severity && (
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${severityPillClass[severity]}`}>
            {opportunity.category}
          </span>
        )}
        {isSnoozed && (
          <BellOff size={14} className="text-snooze-purple ml-auto" />
        )}
      </div>

      {/* Location */}
      <div className="flex gap-1 items-center text-sm text-gray-700">
        <MapPinIcon size={14} className="shrink-0"/>
        <span className="font-medium truncate">{locationLabel}</span>
      </div>

      {/* Dates row */}
      <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-gray-500">
        {opportunity.identified_date && (
          <span>
            Identified: {format(parseISO(opportunity.identified_date), "LLL do, yyyy")}
          </span>
        )}
        {opportunity.last_detected_date && (
          <span>
            Last detected: {format(parseISO(opportunity.last_detected_date), "LLL do, yyyy")}
          </span>
        )}
        <span>
          {opportunity.number_of_detections} detection{opportunity.number_of_detections === 1 ? '' : 's'}
        </span>
      </div>

      {/* Actions */}
      <div className="flex flex-row gap-2 pt-1 border-t border-gray-100">
        <button
          title="Open issue in new tab"
          className="p-1 bg-gray-100 hover:bg-gray-200 rounded-md"
          onClick={() => openOpportunity(opportunity)}
        >
          <ExternalLinkIcon size={16}/>
        </button>
        <StaffOnly>
          <button
            title="Change issue status"
            className="p-1 bg-gray-100 hover:bg-gray-200 rounded-md"
            onClick={() => openChangeOpportunityStatusModal(opportunity)}
          >
            <ArrowRightLeftIcon size={16}/>
          </button>
        </StaffOnly>
        <button
          title="Rate this alert"
          className="p-1 bg-gray-100 hover:bg-gray-200 rounded-md"
          onClick={() => setFeedbackOpen(true)}
        >
          <ThumbsUp size={16}/>
        </button>
      </div>
    </Card>

      {feedbackOpen && (
        <UsefulFeedbackModal
          opportunity={opportunity}
          onClose={() => setFeedbackOpen(false)}
        />
      )}
    </>
  );
};

// ─── Rest of file unchanged ───────────────────────────────────────────────────

/** Sort red severity first, then yellow, then none. */
const severityOrder = (opp: Opportunity): number => {
  const s = getEffectiveSeverity(opp);
  if (s === "red") return 0;
  if (s === "yellow") return 1;
  return 2;
};

const isSnoozed = (o: Opportunity, snoozedLocationIds: Set<number>, snoozedEquipmentIds: Set<number>) =>
  snoozedLocationIds.has(o.location.id) || snoozedEquipmentIds.has(o.installation.equipment);

const ColumnCards = ({ status, snoozedLocationIds, snoozedEquipmentIds }: { status: OpportunityStatus, snoozedLocationIds: Set<number>, snoozedEquipmentIds: Set<number> }) => {
  const { filters, sorting } = useOpportunityManagement();
  const { data: { results: opportunities } } = useOpportunities({
    page: 1,
    pageSize: 1000,
    sorting,
    filters: { ...filters, status: [status.name] },
  });

  const nonSnoozed = [...opportunities.filter(o => !isSnoozed(o, snoozedLocationIds, snoozedEquipmentIds))]
    .sort((a, b) => severityOrder(a) - severityOrder(b));
  const snoozed = [...opportunities.filter(o => isSnoozed(o, snoozedLocationIds, snoozedEquipmentIds))]
    .sort((a, b) => severityOrder(a) - severityOrder(b));

  return (
    <>
      {nonSnoozed.map((opportunity) => (
        <OpportunityCard key={opportunity.id} opportunity={opportunity} />
      ))}
      {snoozed.map((opportunity) => (
        <OpportunityCard key={opportunity.id} opportunity={opportunity} isSnoozed />
      ))}
    </>
  );
};

const OpportunitiesCount = ({ status }: { status: OpportunityStatus }) => {
  const { filters, sorting } = useOpportunityManagement();
  const { data: { results: opportunities } } = useOpportunities({
    page: 1,
    pageSize: 1000,
    sorting,
    filters: { ...filters, status: [status.name] },
  });

  return (
    <span>{opportunities.length} {opportunities.length === 1 ? "issue" : "issues"}</span>
  );
};

const Column = ({ status, snoozedLocationIds, snoozedEquipmentIds }: { status: OpportunityStatus, snoozedLocationIds: Set<number>, snoozedEquipmentIds: Set<number> }) => {
  const [open, setOpen] = useState(true);
  const [isRotated, setIsRotated] = useState(false);

  return (
    <Collapsible open={open} onOpenChange={setOpen} asChild>
      <div className="transition-all group bg-gray-50 rounded-md data-[state=open]:grow basis-0 flex flex-col gap-4 p-2">
        <div className="flex gap-2 flex-wrap justify-between items-start">
          <div className="lg:group-data-[state=closed]:order-2">
            <h2 className="text-lg">{status.name}</h2>
            <div className="text-xs text-gray-500">
              <Suspense fallback="Loading">
                <OpportunitiesCount status={status}/>
              </Suspense>
            </div>
          </div>
          <div className="lg:group-data-[state=closed]:order-1 flex gap-2 items-center">
            <QuickTooltip text={status.description_internal}>
              <InfoIcon size={16} className="text-gray-500"/>
            </QuickTooltip>
            <CollapsibleTrigger asChild>
              <button
                title="Hide column"
                className="p-1 bg-gray-100 hover:bg-gray-200 rounded-md transition-all text-gray-500"
                onClick={() => { setIsRotated(!isRotated); setOpen(!open); }}
              >
                <div className={`transition-all ${isRotated ? 'rotate-180' : ''}`}>
                  {open ? <Minimize2Icon size={16}/> : <Maximize2Icon size={16}/>}
                </div>
              </button>
            </CollapsibleTrigger>
          </div>
        </div>
        <CollapsibleContent>
          <div className="data-[state=closed]:hidden grow flex flex-col gap-2">
            <Suspense fallback={<Spinner/>}>
              <ColumnCards status={status} snoozedLocationIds={snoozedLocationIds} snoozedEquipmentIds={snoozedEquipmentIds}/>
            </Suspense>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};

const SortingSelector = ({
  sorting,
  setSorting,
}: {
  sorting: SortingOption[];
  setSorting: Dispatch<SetStateAction<SortingOption[]>>;
}) => {
  const currentSortingOption = sorting[0];
  return (
    <div className="flex flex-row gap-2 items-center w-fit">
      <Select
        key={currentSortingOption?.id}
        value={currentSortingOption?.id}
        onValueChange={value => setSorting([{ id: value, desc: true }])}
      >
        <SelectTrigger>
          <SelectValue placeholder="Select option"/>
        </SelectTrigger>
        <SelectContent>
          {ALL_SORTING_OPTIONS.map((sortingOption) => (
            <SelectItem key={sortingOption.value} value={sortingOption.value}>
              {sortingOption.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {currentSortingOption && (
        <>
          <button
            title="Toggle ascending/descending"
            className="p-1 bg-gray-100 hover:bg-gray-200 rounded-md"
            onClick={() => setSorting([{ id: currentSortingOption.id, desc: !currentSortingOption.desc }])}
          >
            {currentSortingOption.desc ? <ChevronDown size={16}/> : <ChevronUp size={16}/>}
          </button>
          <button
            title="Clear sorting"
            className="p-1 bg-gray-100 hover:bg-gray-200 rounded-md"
            onClick={() => setSorting([])}
          >
            <XIcon size={16} className="text-gray-500"/>
          </button>
        </>
      )}
    </div>
  );
};

const OpportunityManagementBoard = ({ organizationId, snoozedLocationIds, snoozedEquipmentIds }: { organizationId?: number, snoozedLocationIds: Set<number>, snoozedEquipmentIds: Set<number> }) => {
  const { filters, sorting, setSorting } = useOpportunityManagement();
  const { data: statusOptions } = useOpportunityStatuses();

  const statusColumnsToShow = filters.status?.length
    ? statusOptions.filter(status => filters.status?.includes(status.name))
    : statusOptions;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-row flex-wrap gap-3">
        <div className="flex flex-col gap-1">
          <span className="text-xs">View</span>
          <OpportunityManagementViewSelector/>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs">Filters</span>
          <Filters organizationId={organizationId}/>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs">Sort</span>
          <SortingSelector sorting={sorting} setSorting={setSorting}/>
        </div>
      </div>
      <div className="flex flex-col lg:flex-row gap-2 w-full">
        {statusColumnsToShow.map((status, index) => (
          <Column status={status} key={index} snoozedLocationIds={snoozedLocationIds} snoozedEquipmentIds={snoozedEquipmentIds}/>
        ))}
      </div>
    </div>
  );
};

export default OpportunityManagementBoard;